// src/utils/firebase/psa.js
import { 
  collection, 
  addDoc,
  getDocs,
  getDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase.utils';
import { serializeFirestoreData } from './serialization';
import { 
  SERVICE_TURNAROUND_DAYS,
  PSA_STATUSES
} from '../psa/psa.constants';

/**
 * Calculates expected return date based on service level and submission date
 * @param {string} serviceLevel - Service level string
 * @param {Timestamp} submissionDate - Submission date timestamp
 * @returns {Timestamp|null} Expected return date or null if service level not found
 */
const calculateExpectedReturnDate = (serviceLevel, submissionDate) => {
  if (!serviceLevel || !submissionDate) return null;
  
  const turnaroundDays = SERVICE_TURNAROUND_DAYS[serviceLevel];
  if (!turnaroundDays) return null;
  
  // Convert Firestore Timestamp to Date, add business days, convert back
  const submissionDateObj = submissionDate.toDate();
  const expectedDate = new Date(submissionDateObj);
  
  // Add business days (excluding weekends)
  let daysAdded = 0;
  while (daysAdded < turnaroundDays) {
    expectedDate.setDate(expectedDate.getDate() + 1);
    // Skip weekends (Saturday = 6, Sunday = 0)
    if (expectedDate.getDay() !== 0 && expectedDate.getDay() !== 6) {
      daysAdded++;
    }
  }
  
  return Timestamp.fromDate(expectedDate);
};

/**
 * Adds a PSA card submission to Firestore
 * @param {string} userID - User ID
 * @param {Object} cardData - Card data from ChecklistItemDisplay
 * @param {Object} gradingData - Grading data from NewGradingModal
 * @returns {Promise<string>} Document ID of the saved PSA card
 */
export const addPSACard = async (userID, cardData, gradingData) => {
  try {
    const psaRef = collection(db, `users/${userID}/psa`);
    
    const submissionDate = Timestamp.now();
    const serviceLevel = gradingData.type === 'new' ? gradingData.service : null;
    const grade = gradingData.type === 'graded' ? gradingData.grade : null;
    
    // Default status to 'Draft' for new cards if no status is provided
    // Draft cards (type: 'draft') also default to 'Draft'
    const initialStatus = PSA_STATUSES.DRAFT;
    
    // Calculate expected return date only for new submissions (not drafts)
    const expectedReturnDate = (gradingData.type === 'new' && serviceLevel)
      ? calculateExpectedReturnDate(serviceLevel, submissionDate)
      : null;
    
    const psaCardData = {
      // Card identification
      cardNumber: cardData.cardNumber,
      cardName: cardData.name || [],
      cardTeam: cardData.team || [],
      cardAttributes: cardData.cardAttributes || [],
      cardPath: {
        sport: cardData.cardPath?.[0] || '',
        year: cardData.cardPath?.[1] || '',
        manufacturer: cardData.cardPath?.[2] || '',
        series: cardData.cardPath?.[3] || '',
        cardType: cardData.cardPath?.[4] || '',
        cardTypeCategory: cardData.cardPath?.[5] || ''
      },
      
      // Variation/Parallel info
      selectedVariation: cardData.selectedVariation || null,
      
      // Grading info
      grade: grade ? (grade.replace(/\D/g, '') || null) : null, // Extract just the number (e.g., "10" from "PSA 10" or "10")
      serviceLevel: serviceLevel,
      
      // Status tracking
      status: initialStatus,
      submissionDate: gradingData.type === 'new' ? submissionDate : null,
      expectedReturnDate: expectedReturnDate,
      trackingNumber: null,
      dateReturned: null,
      
      // Metadata
      createdAt: submissionDate,
      updatedAt: submissionDate,
      notes: ''
    };
    
    // Add document to PSA collection
    const docRef = await addDoc(psaRef, psaCardData);
    
    return docRef.id;
  } catch (error) {
    console.error('Error saving PSA card:', error);
    throw error;
  }
};

/**
 * Fetches all PSA cards for a user
 * @param {string} userID - User ID
 * @returns {Promise<Object>} Object with document IDs as keys and card data as values
 */
export const fetchPSACards = async (userID) => {
  try {
    const psaRef = collection(db, `users/${userID}/psa`);
    const q = query(psaRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    const psaCards = {};
    snapshot.forEach((doc) => {
      const data = serializeFirestoreData(doc.data());
      psaCards[doc.id] = {
        id: doc.id,
        ...data
      };
    });
    
    return psaCards;
  } catch (error) {
    console.error('Error fetching PSA cards:', error);
    throw error;
  }
};

/**
 * Sets up a real-time listener for PSA cards
 * @param {string} userID - User ID
 * @param {Function} callback - Callback function that receives the PSA cards object
 * @returns {Function} Unsubscribe function
 */
export const listenToPSACards = (userID, callback) => {
  try {
    const psaRef = collection(db, `users/${userID}/psa`);
    const q = query(psaRef, orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const psaCards = {};
      snapshot.forEach((doc) => {
        const data = serializeFirestoreData(doc.data());
        psaCards[doc.id] = {
          id: doc.id,
          ...data
        };
      });
      callback(psaCards);
    }, (error) => {
      console.error('Error listening to PSA cards:', error);
      callback({});
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up PSA cards listener:', error);
    callback({});
    return () => {}; // Return no-op unsubscribe function
  }
};

/**
 * Updates a PSA card in Firestore
 * @param {string} userID - User ID
 * @param {string} cardID - PSA card document ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updatePSACard = async (userID, cardID, updates) => {
  try {
    const cardDocRef = doc(db, `users/${userID}/psa`, cardID);
    
    // Add updatedAt timestamp
    const updateData = {
      ...updates,
      updatedAt: Timestamp.now()
    };
    
    await updateDoc(cardDocRef, updateData);
  } catch (error) {
    console.error('Error updating PSA card:', error);
    throw error;
  }
};

/**
 * Deletes a PSA card from Firestore
 * @param {string} userID - User ID
 * @param {string} cardID - PSA card document ID
 * @returns {Promise<void>}
 */
export const deletePSACard = async (userID, cardID) => {
  try {
    const cardDocRef = doc(db, `users/${userID}/psa`, cardID);
    await deleteDoc(cardDocRef);
  } catch (error) {
    console.error('Error deleting PSA card:', error);
    throw error;
  }
};

// ==================== SUBMISSION FUNCTIONS ====================

/**
 * Creates a new PSA submission
 * @param {string} userID - User ID
 * @param {Object} submissionData - Submission data
 * @param {string} submissionData.psaSubmissionID - Unique PSA submission ID
 * @param {string} submissionData.serviceLevel - Service level
 * @param {string} submissionData.status - Initial status (defaults to Draft)
 * @returns {Promise<string>} Document ID of the created submission
 */
export const createPSASubmission = async (userID, submissionData) => {
  try {
    const submissionsRef = collection(db, `users/${userID}/psaSubmissions`);
    
    const now = Timestamp.now();
    const serviceLevel = submissionData.serviceLevel;
    const submissionDate = submissionData.submissionDate || now;
    
    // Calculate expected return date if service level is provided
    const expectedReturnDate = serviceLevel
      ? calculateExpectedReturnDate(serviceLevel, submissionDate)
      : null;
    
    const submissionDoc = {
      psaSubmissionID: submissionData.psaSubmissionID || '',
      serviceLevel: serviceLevel || null,
      status: submissionData.status || PSA_STATUSES.DRAFT,
      trackingNumber: submissionData.trackingNumber || null,
      psaDropOffDateTime: submissionData.psaDropOffDateTime || null,
      submissionDate: submissionDate,
      expectedReturnDate: expectedReturnDate,
      psaEventDropOff: submissionData.psaEventDropOff || false,
      shipToPSA: submissionData.shipToPSA || false,
      notes: submissionData.notes || '',
      createdAt: now,
      updatedAt: now
    };
    
    const docRef = await addDoc(submissionsRef, submissionDoc);
    return docRef.id;
  } catch (error) {
    console.error('Error creating PSA submission:', error);
    throw error;
  }
};

/**
 * Adds a card to an existing submission or creates a new submission
 * @param {string} userID - User ID
 * @param {Object} cardData - Card data
 * @param {Object} gradingData - Grading data
 * @param {string} submissionID - Optional existing submission ID
 * @param {Object} submissionData - Optional submission data for new submission
 * @returns {Promise<{cardID: string, submissionID: string}>}
 */
export const addCardToSubmission = async (userID, cardData, gradingData, submissionID = null, submissionData = {}) => {
  try {
    let finalSubmissionID = submissionID;
    
    // Create submission if not provided
    if (!finalSubmissionID) {
      const serviceLevel = gradingData.type === 'new' ? gradingData.service : null;
      finalSubmissionID = await createPSASubmission(userID, {
        ...submissionData,
        serviceLevel: serviceLevel,
        status: PSA_STATUSES.DRAFT
      });
    }
    
    // Add card to submission
    const psaRef = collection(db, `users/${userID}/psa`);
    const submissionDate = Timestamp.now();
    const serviceLevel = gradingData.type === 'new' ? gradingData.service : null;
    const grade = gradingData.type === 'graded' ? gradingData.grade : null;
    
    const psaCardData = {
      // Card identification
      cardNumber: cardData.cardNumber,
      cardName: cardData.name || [],
      cardTeam: cardData.team || [],
      cardAttributes: cardData.cardAttributes || [],
      cardPath: {
        sport: cardData.cardPath?.[0] || '',
        year: cardData.cardPath?.[1] || '',
        manufacturer: cardData.cardPath?.[2] || '',
        series: cardData.cardPath?.[3] || '',
        cardType: cardData.cardPath?.[4] || '',
        cardTypeCategory: cardData.cardPath?.[5] || ''
      },
      
      // Variation/Parallel info
      selectedVariation: cardData.selectedVariation || null,
      
      // Grading info
      grade: grade ? (grade.replace(/\D/g, '') || null) : null,
      serviceLevel: serviceLevel,
      
      // Submission reference
      submissionID: finalSubmissionID,
      
      // Status tracking (inherited from submission, but can be overridden per card)
      status: PSA_STATUSES.DRAFT,
      submissionDate: gradingData.type === 'new' ? submissionDate : null,
      expectedReturnDate: null, // Will be inherited from submission
      trackingNumber: null, // Will be inherited from submission
      dateReturned: null,
      
      // Metadata
      createdAt: submissionDate,
      updatedAt: submissionDate,
      notes: ''
    };
    
    const docRef = await addDoc(psaRef, psaCardData);
    
    return {
      cardID: docRef.id,
      submissionID: finalSubmissionID
    };
  } catch (error) {
    console.error('Error adding card to submission:', error);
    throw error;
  }
};

/**
 * Updates a PSA submission and all cards in it
 * @param {string} userID - User ID
 * @param {string} submissionID - Submission document ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updatePSASubmission = async (userID, submissionID, updates) => {
  try {
    const batch = writeBatch(db);
    
    // Update submission
    const submissionDocRef = doc(db, `users/${userID}/psaSubmissions`, submissionID);
    const submissionUpdateData = {
      ...updates,
      updatedAt: Timestamp.now()
    };
    batch.update(submissionDocRef, submissionUpdateData);
    
    // Get all cards in this submission
    const cardsRef = collection(db, `users/${userID}/psa`);
    const cardsQuery = query(cardsRef, where('submissionID', '==', submissionID));
    const cardsSnapshot = await getDocs(cardsQuery);
    
    // Update all cards with submission-level fields
    const cardUpdates = {
      status: updates.status,
      trackingNumber: updates.trackingNumber,
      psaDropOffDateTime: updates.psaDropOffDateTime,
      psaEventDropOff: updates.psaEventDropOff,
      shipToPSA: updates.shipToPSA,
      expectedReturnDate: updates.expectedReturnDate,
      updatedAt: Timestamp.now()
    };
    
    // Remove undefined/null fields from cardUpdates
    Object.keys(cardUpdates).forEach(key => {
      if (cardUpdates[key] === undefined) {
        delete cardUpdates[key];
      }
    });
    
    cardsSnapshot.forEach((cardDoc) => {
      const cardDocRef = doc(db, `users/${userID}/psa`, cardDoc.id);
      batch.update(cardDocRef, cardUpdates);
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Error updating PSA submission:', error);
    throw error;
  }
};

/**
 * Deletes a PSA submission and removes submissionID from all associated cards
 * @param {string} userID - User ID
 * @param {string} submissionID - Submission document ID
 * @returns {Promise<void>}
 */
export const deletePSASubmission = async (userID, submissionID) => {
  try {
    const batch = writeBatch(db);
    
    // Get all cards in this submission
    const cardsRef = collection(db, `users/${userID}/psa`);
    const cardsQuery = query(cardsRef, where('submissionID', '==', submissionID));
    const cardsSnapshot = await getDocs(cardsQuery);
    
    // Remove submissionID from all cards (unassign them)
    cardsSnapshot.forEach((cardDoc) => {
      const cardDocRef = doc(db, `users/${userID}/psa`, cardDoc.id);
      batch.update(cardDocRef, {
        submissionID: null,
        updatedAt: Timestamp.now()
      });
    });
    
    // Delete the submission document
    const submissionDocRef = doc(db, `users/${userID}/psaSubmissions`, submissionID);
    batch.delete(submissionDocRef);
    
    await batch.commit();
  } catch (error) {
    console.error('Error deleting PSA submission:', error);
    throw error;
  }
};

/**
 * Sets up a real-time listener for PSA submissions
 * @param {string} userID - User ID
 * @param {Function} callback - Callback function that receives the submissions object
 * @returns {Function} Unsubscribe function
 */
export const listenToPSASubmissions = (userID, callback) => {
  try {
    const submissionsRef = collection(db, `users/${userID}/psaSubmissions`);
    const q = query(submissionsRef, orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const submissions = {};
      snapshot.forEach((doc) => {
        const data = serializeFirestoreData(doc.data());
        submissions[doc.id] = {
          id: doc.id,
          ...data
        };
      });
      callback(submissions);
    }, (error) => {
      console.error('Error listening to PSA submissions:', error);
      callback({});
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up PSA submissions listener:', error);
    callback({});
    return () => {}; // Return no-op unsubscribe function
  }
};

/**
 * Gets all cards for a specific submission
 * @param {string} userID - User ID
 * @param {string} submissionID - Submission document ID
 * @returns {Promise<Object>} Object with document IDs as keys and card data as values
 */
export const getCardsBySubmission = async (userID, submissionID) => {
  try {
    const cardsRef = collection(db, `users/${userID}/psa`);
    const q = query(cardsRef, where('submissionID', '==', submissionID), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    const cards = {};
    snapshot.forEach((doc) => {
      const data = serializeFirestoreData(doc.data());
      cards[doc.id] = {
        id: doc.id,
        ...data
      };
    });
    
    return cards;
  } catch (error) {
    console.error('Error fetching cards by submission:', error);
    throw error;
  }
};

/**
 * Assigns an existing card to a submission
 * @param {string} userID - User ID
 * @param {string} cardID - Card document ID
 * @param {string} submissionID - Submission document ID
 * @returns {Promise<void>}
 */
export const assignCardToSubmission = async (userID, cardID, submissionID) => {
  try {
    const cardDocRef = doc(db, `users/${userID}/psa`, cardID);
    
    // Get submission to inherit status
    const submissionDocRef = doc(db, `users/${userID}/psaSubmissions`, submissionID);
    const submissionDoc = await getDoc(submissionDocRef);
    
    let submissionStatus = PSA_STATUSES.DRAFT;
    if (submissionDoc.exists()) {
      const submissionData = submissionDoc.data();
      submissionStatus = submissionData.status || PSA_STATUSES.DRAFT;
    }
    
    await updateDoc(cardDocRef, {
      submissionID: submissionID,
      status: submissionStatus, // Inherit submission status
      updatedAt: Timestamp.now()
    });
    
  } catch (error) {
    console.error('Error assigning card to submission:', error);
    throw error;
  }
};
